import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { IsAdmin } from 'app/IsAdmin'
import { IsNotAdmin } from 'app/IsNotAdmin'
import EmptyLogo from 'assets/images/bvaDetails/emptyLogo.png'
import { Spinner } from 'components/Spinner'
import { BVAList } from 'model/BVAList'
import { buildBvaDetailsPath } from 'utils/bvaSlug'

import './brandTable.scss'
import { NoResult } from './NoResult'

interface Props {
  brands: BVAList[]
  loadMore: () => void
  hasNextPage?: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  appliedSearch: string
  totalResults: number
}

const BrandTable: React.FC<Props> = ({
  brands,
  loadMore,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  totalResults,
  appliedSearch,
}) => {
  const navigate = useNavigate()

  const [hoverRow, setHoverRow] = useState<number | null>(null)
  const [hoverLastCol, setHoverLastCol] = useState(false)

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Current'
      case 'ARCHIVE':
        return 'Previous'
      default:
        return status
    }
  }
  const getAdminStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Current'
      case 'ARCHIVE':
        return 'Previous'
      case 'DRAFT':
        return 'Draft'
      default:
        return status
    }
  }
  if (isLoading) {
    return (
      <div className="mx-auto basis-full flex justify-center items-center mt-20">
        <Spinner size={32} color="#000000" />
      </div>
    )
  }

  if (brands.length === 0) {
    return <NoResult />
  }

  return (
    <div className="w-full">
      {/* RESULTS COUNT */}
      {appliedSearch && (
        <div className="results-wrapper mb-6">
          <div className="subtitle-xl">{totalResults}</div>
          <div className="body-m"> results for "{appliedSearch}"</div>
        </div>
      )}
      <div className="table-wrapper">
        <table className="brand-table">
          <thead>
            <tr>
              <th className="brand-col subtitle-xs">Brand</th>
              <th className="subtitle-xs version-col">Version</th>
              <th className="subtitle-xs">Year</th>
              <th className="subtitle-xs">Category</th>
              <IsNotAdmin>
                <th className="owner-col subtitle-xs" colSpan={2}>
                  Owner
                </th>
              </IsNotAdmin>
              <IsAdmin>
                <th className="owner-col subtitle-xs">Owner</th>
                <th className="subtitle-xs">Actions</th>
              </IsAdmin>
            </tr>
          </thead>

          <tbody className="clickable-row">
            {brands.map((item: BVAList, rowIndex: number) => (
              <tr
                key={item.brandId}
                onMouseLeave={() => {
                  setHoverRow(null)
                  setHoverLastCol(false)
                }}
                className="table-row"
                onClick={() => navigate(buildBvaDetailsPath(item.id, item.brandName))}
              >
                <td
                  className={`brand-col ${hoverRow === rowIndex && !hoverLastCol ? 'row-hover' : ''}`}
                  onMouseEnter={() => {
                    setHoverRow(rowIndex)
                    setHoverLastCol(false)
                  }}
                >
                  <div className="brand-logo-wrapper">
                    <img
                      src={item.logoUrl ? item.logoUrl : item.brandLogoUrl ? item.brandLogoUrl : EmptyLogo}
                      alt={item.brandName}
                      className="brand-logo"
                    />
                    <div className="brand-cell body-s">{item.brandName}</div>
                  </div>
                </td>

                <td
                  className={`body-s ${hoverRow === rowIndex && !hoverLastCol ? 'row-hover' : ''}`}
                  onMouseEnter={() => {
                    setHoverRow(rowIndex)
                    setHoverLastCol(false)
                  }}
                >
                  <div className="w-28 inline-flex justify-start items-center gap-4">
                    <IsAdmin>
                      <div
                        className={`p-2 rounded-lg flex justify-center items-center gap-1 ${
                          getAdminStatusLabel(item.versionStatus) === 'Current'
                            ? 'bg-[#CDEDA6]'
                            : getAdminStatusLabel(item.versionStatus) === 'Draft'
                              ? 'bg-[#ABE6FF]'
                              : 'bg-[#D4D4D4]'
                        } 
                        ${item.versionStatus.toLowerCase()} ${hoverRow === rowIndex && hoverLastCol ? 'last-hover' : ''}`}
                        onMouseEnter={() => {
                          setHoverRow(rowIndex)
                          setHoverLastCol(true)
                        }}
                      >
                        <div className="justify-start color-neutral-80 subtitle-xs leading-4">
                          {getAdminStatusLabel(item.versionStatus)}
                        </div>
                      </div>
                    </IsAdmin>
                    <IsNotAdmin>
                      <div
                        className={`p-2 rounded-lg flex justify-center items-center gap-1 ${
                          getStatusLabel(item.versionStatus) === 'Current' ? 'bg-[#CDEDA6]' : 'bg-[#D4D4D4]'
                        } 
                        ${item.versionStatus.toLowerCase()} ${hoverRow === rowIndex && hoverLastCol ? 'last-hover' : ''}`}
                        onMouseEnter={() => {
                          setHoverRow(rowIndex)
                          setHoverLastCol(true)
                        }}
                      >
                        <div className="justify-start color-neutral-80 subtitle-xs leading-4">
                          {getStatusLabel(item.versionStatus)}
                        </div>
                      </div>
                    </IsNotAdmin>
                  </div>
                </td>

                <td
                  className={`body-s ${hoverRow === rowIndex && !hoverLastCol ? 'row-hover' : ''}`}
                  onMouseEnter={() => {
                    setHoverRow(rowIndex)
                    setHoverLastCol(false)
                  }}
                >
                  {item.year}
                </td>
                <td
                  className={`body-s ${hoverRow === rowIndex && !hoverLastCol ? 'row-hover' : ''}`}
                  onMouseEnter={() => {
                    setHoverRow(rowIndex)
                    setHoverLastCol(false)
                  }}
                >
                  {item.category}
                </td>

                <td
                  className={`owner-col truncate body-s ${hoverRow === rowIndex && !hoverLastCol ? 'row-hover' : ''}`}
                  onMouseEnter={() => {
                    setHoverRow(rowIndex)
                    setHoverLastCol(false)
                  }}
                >
                  {item.creatorName}
                </td>
                {/* <IsNotAdmin> */}
                <td
                  className={`body-s ${hoverRow === rowIndex && !hoverLastCol ? 'row-hover' : ''}`}
                  onMouseEnter={() => {
                    setHoverRow(rowIndex)
                    setHoverLastCol(false)
                  }}
                >
                  <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.3 6L1.7 0.3C1.3 -0.1 0.7 -0.1 0.3 0.3C-0.1 0.7 -0.1 1.3 0.3 1.7L5.2 6.6L0.3 11.5C0.1 11.7 0 11.9 0 12.2C0 12.8 0.4 13.2 1 13.2C1.3 13.2 1.5 13.1 1.7 12.9L7.4 7.2C7.7 7 7.7 6.4 7.3 6Z"
                      fill="black"
                    />
                  </svg>
                </td>
                {/* </IsNotAdmin> */}
                {/* <IsAdmin>
                  <td
                    className={`actions body-s ${hoverRow === rowIndex && hoverLastCol ? 'last-hover' : ''}`}
                    onMouseEnter={() => {
                      setHoverRow(rowIndex)
                      setHoverLastCol(true)
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    <Button
                      disabled={item.versionStatus.toLowerCase() === 'archive'}
                      color="white"
                      border={false}
                      className="edit-button subtitle-s !font-semibold"
                      onClick={() => navigate('/BVAFrameworks/create')}
                      text="Edit"
                      icon={<img src={PenIcon} alt="Create" className="ml-2" width="16" height="16" />}
                    />
                  </td>
                </IsAdmin> */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* LOAD MORE */}

      <div className="flex justify-center mt-10">
        {hasNextPage ? (
          <button
            onClick={loadMore}
            className="flex items-center gap-2 font-medium text-gray-700"
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage && (
              <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.0035 10.8333C3.94883 10.7385 3.876 10.6554 3.78918 10.5887C3.70236 10.522 3.60324 10.4731 3.49751 10.4448C3.39177 10.4164 3.28148 10.4092 3.17295 10.4235C3.06442 10.4378 2.95978 10.4734 2.865 10.5282L1.42132 11.3615C1.22999 11.4721 1.09041 11.6541 1.03325 11.8675C0.976093 12.081 1.00604 12.3084 1.11651 12.4998C1.22698 12.6912 1.40893 12.8308 1.62236 12.8881C1.83578 12.9454 2.06321 12.9155 2.25465 12.8052L3.69833 11.9718C3.88968 11.8612 4.0293 11.6792 4.08652 11.4657C4.14374 11.2523 4.11388 11.0248 4.0035 10.8333ZM1.42131 5.30517L2.86499 6.13851C2.95979 6.19329 3.06445 6.22886 3.173 6.24318C3.28154 6.25751 3.39185 6.2503 3.49761 6.22198C3.60337 6.19366 3.70252 6.14478 3.78939 6.07813C3.87625 6.01148 3.94913 5.92837 4.00387 5.83355C4.0586 5.73872 4.09412 5.63404 4.10839 5.52549C4.12265 5.41693 4.11539 5.30663 4.08702 5.20088C4.05864 5.09513 4.00971 4.99601 3.94302 4.90918C3.87633 4.82235 3.79318 4.74951 3.69833 4.69482L2.25464 3.86149C2.0632 3.75112 1.83577 3.72129 1.62235 3.77855C1.40892 3.83582 1.22697 3.9755 1.1165 4.16688C1.00603 4.35826 0.976084 4.58568 1.03324 4.79913C1.0904 5.01259 1.22998 5.19461 1.42131 5.30517ZM3.33333 8.33333C3.33333 8.11232 3.24554 7.90036 3.08926 7.74408C2.93298 7.5878 2.72101 7.5 2.5 7.5H0.833333C0.61232 7.5 0.400358 7.5878 0.244078 7.74408C0.0877973 7.90036 0 8.11232 0 8.33333C0 8.55435 0.0877973 8.76631 0.244078 8.92259C0.400358 9.07887 0.61232 9.16667 0.833333 9.16667H2.5C2.72101 9.16667 2.93298 9.07887 3.08926 8.92259C3.24554 8.76631 3.33333 8.55435 3.33333 8.33333ZM12.6632 5.83333C12.7737 6.0247 12.9558 6.16434 13.1693 6.22157C13.3827 6.27879 13.6102 6.24892 13.8017 6.13851L15.2454 5.30517C15.3402 5.25049 15.4234 5.17765 15.4901 5.09082C15.5567 5.00399 15.6057 4.90486 15.6341 4.79912C15.6624 4.69337 15.6697 4.58307 15.6554 4.47451C15.6412 4.36596 15.6056 4.26128 15.5509 4.16645C15.4962 4.07163 15.4233 3.98852 15.3364 3.92187C15.2496 3.85522 15.1504 3.80634 15.0446 3.77802C14.9389 3.7497 14.8286 3.74249 14.72 3.75682C14.6115 3.77114 14.5068 3.80671 14.412 3.86149L12.9683 4.69482C12.777 4.80542 12.6374 4.98745 12.5802 5.20093C12.5229 5.4144 12.5528 5.64186 12.6632 5.83333ZM10.8333 4.0035C11.0248 4.11391 11.2523 4.14378 11.4657 4.08656C11.6792 4.02934 11.8613 3.8897 11.9718 3.69833L12.8052 2.25465C12.86 2.15985 12.8955 2.05519 12.9099 1.94664C12.9242 1.8381 12.917 1.72779 12.8886 1.62203C12.8603 1.51627 12.8114 1.41712 12.7448 1.33026C12.6781 1.24339 12.595 1.17051 12.5002 1.11577C12.4054 1.06104 12.3007 1.02552 12.1922 1.01125C12.0836 0.996987 11.9733 1.00425 11.8675 1.03262C11.7618 1.061 11.6627 1.10993 11.5758 1.17662C11.489 1.24332 11.4162 1.32646 11.3615 1.42132L10.5282 2.865C10.4178 3.05647 10.3879 3.28393 10.4451 3.4974C10.5024 3.71087 10.642 3.8929 10.8333 4.0035ZM15.2454 11.3615L13.8017 10.5282C13.7069 10.4734 13.6022 10.4378 13.4937 10.4235C13.3851 10.4092 13.2748 10.4164 13.1691 10.4447C13.0633 10.473 12.9641 10.5219 12.8773 10.5885C12.7904 10.6552 12.7175 10.7383 12.6628 10.8331C12.6081 10.9279 12.5725 11.0326 12.5583 11.1412C12.544 11.2497 12.5513 11.36 12.5796 11.4658C12.608 11.5715 12.657 11.6707 12.7236 11.7575C12.7903 11.8443 12.8735 11.9172 12.9683 11.9718L14.412 12.8052C14.6035 12.9155 14.8309 12.9454 15.0443 12.8881C15.2577 12.8308 15.4397 12.6912 15.5502 12.4998C15.6606 12.3084 15.6906 12.081 15.6334 11.8675C15.5763 11.6541 15.4367 11.4721 15.2454 11.3615ZM11.9718 12.9683C11.9172 12.8735 11.8443 12.7903 11.7575 12.7236C11.6707 12.6569 11.5715 12.608 11.4658 12.5796C11.36 12.5513 11.2497 12.544 11.1412 12.5583C11.0326 12.5725 10.9279 12.6081 10.8331 12.6628C10.7383 12.7175 10.6552 12.7904 10.5885 12.8773C10.5219 12.9641 10.473 13.0633 10.4447 13.169C10.4164 13.2748 10.4092 13.3851 10.4235 13.4937C10.4378 13.6022 10.4734 13.7069 10.5282 13.8017L11.3615 15.2453C11.4162 15.3402 11.489 15.4234 11.5758 15.49C11.6627 15.5567 11.7618 15.6057 11.8675 15.634C11.9733 15.6624 12.0836 15.6697 12.1922 15.6554C12.3007 15.6411 12.4054 15.6056 12.5002 15.5509C12.595 15.4962 12.6781 15.4233 12.7448 15.3364C12.8114 15.2495 12.8603 15.1504 12.8886 15.0446C12.917 14.9389 12.9242 14.8286 12.9099 14.72C12.8955 14.6115 12.86 14.5068 12.8052 14.412L11.9718 12.9683ZM15.8333 7.5H14.1667C13.9457 7.5 13.7337 7.5878 13.5774 7.74408C13.4211 7.90036 13.3333 8.11232 13.3333 8.33333C13.3333 8.55435 13.4211 8.76631 13.5774 8.92259C13.7337 9.07887 13.9457 9.16667 14.1667 9.16667H15.8333C16.0543 9.16667 16.2663 9.07887 16.4226 8.92259C16.5789 8.76631 16.6667 8.55435 16.6667 8.33333C16.6667 8.11232 16.5789 7.90036 16.4226 7.74408C16.2663 7.5878 16.0543 7.5 15.8333 7.5ZM8.33333 13.3333C8.11232 13.3333 7.90036 13.4211 7.74408 13.5774C7.5878 13.7337 7.5 13.9457 7.5 14.1667V15.8333C7.5 16.0543 7.5878 16.2663 7.74408 16.4226C7.90036 16.5789 8.11232 16.6667 8.33333 16.6667C8.55435 16.6667 8.76631 16.5789 8.92259 16.4226C9.07887 16.2663 9.16667 16.0543 9.16667 15.8333V14.1667C9.16667 13.9457 9.07887 13.7337 8.92259 13.5774C8.76631 13.4211 8.55435 13.3333 8.33333 13.3333ZM5.83333 12.6632C5.73856 12.6084 5.63391 12.5728 5.52538 12.5585C5.41685 12.5442 5.30656 12.5514 5.20082 12.5798C5.09508 12.6081 4.99597 12.657 4.90914 12.7237C4.82232 12.7904 4.74949 12.8735 4.69482 12.9683L3.86149 14.412C3.75112 14.6035 3.72129 14.8309 3.77855 15.0443C3.83582 15.2577 3.9755 15.4397 4.16688 15.5502C4.35826 15.6606 4.58568 15.6906 4.79913 15.6334C5.01259 15.5763 5.19461 15.4367 5.30517 15.2454L6.13851 13.8017C6.24889 13.6102 6.27875 13.3827 6.22152 13.1693C6.1643 12.9558 6.02468 12.7738 5.83333 12.6632ZM8.33333 0C8.11232 0 7.90036 0.0877973 7.74408 0.244078C7.5878 0.400358 7.5 0.61232 7.5 0.833333V2.5C7.5 2.72101 7.5878 2.93298 7.74408 3.08926C7.90036 3.24554 8.11232 3.33333 8.33333 3.33333C8.55435 3.33333 8.76631 3.24554 8.92259 3.08926C9.07887 2.93298 9.16667 2.72101 9.16667 2.5V0.833333C9.16667 0.61232 9.07887 0.400358 8.92259 0.244078C8.76631 0.0877973 8.55435 0 8.33333 0Z"
                  fill="black"
                />
              </svg>
            )}
            {isFetchingNextPage ? 'Load more' : 'Load more'}
          </button>
        ) : (
          <span className="body-s color-neutral-80">No more results</span>
        )}
      </div>
    </div>
  )
}

export default BrandTable
